<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { useInsightConfigEditor } from '../../../composables/useInsightConfigEditor'

import ConfigClassificationSection from './ConfigClassificationSection.vue'
import ConfigContentSection from './ConfigContentSection.vue'
import ConfigDetectionSection from './ConfigDetectionSection.vue'
import ConfigImpactSection from './ConfigImpactSection.vue'
import ConfigOverviewSection from './ConfigOverviewSection.vue'
import ConfigPipelineSection from './ConfigPipelineSection.vue'
import ConfigVisualizationSection from './ConfigVisualizationSection.vue'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { useConfirm } from '@/shared/ui/feedback'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'

const props = defineProps<{
  configId?: number
  listRouteName?: string
  editRouteName?: string
}>()
const emit = defineEmits<{ saved: [id: number | undefined]; deleted: [] }>()
const { t } = useI18n()
const router = useRouter()
const confirm = useConfirm()
const editor = useInsightConfigEditor({ configId: computed(() => props.configId) })
const title = computed(() =>
  t(
    editor.isCreate.value
      ? 'insights.sysadmin.config.createTitle'
      : 'insights.sysadmin.config.editTitle',
  ),
)

async function save(replace = false): Promise<void> {
  const result = await editor.save(replace ? 'replace' : undefined)
  if (result?.kind !== 'saved') return
  emit('saved', result.config.id)
  if (editor.isCreate.value && result.config.id !== undefined && props.editRouteName) {
    await router.replace({ name: props.editRouteName, params: { configId: result.config.id } })
  }
}

async function remove(): Promise<void> {
  const accepted = await confirm({
    title: t('insights.sysadmin.config.deleteTitle'),
    message: t('insights.sysadmin.config.deleteConfirm'),
    confirmLabel: t('insights.sysadmin.config.delete'),
    cancelLabel: t('insights.sysadmin.config.cancel'),
    danger: true,
  })
  if (!accepted) return
  const outcome = await editor.deleteConfig()
  if (outcome !== 'deleted') return
  emit('deleted')
  if (props.listRouteName) await router.push({ name: props.listRouteName })
}
</script>

<template>
  <main class="config-editor grid gap-lg" data-testid="insight-config-editor">
    <PageHeader :title="title" :subtitle="t('insights.sysadmin.config.editorSubtitle')">
      <template #actions>
        <Button
          v-if="!editor.isCreate.value"
          variant="danger"
          :disabled="editor.isSubmitting.value"
          @click="remove"
        >
          {{ t('insights.sysadmin.config.delete') }}
        </Button>
        <Button
          v-if="!editor.isCreate.value"
          variant="outline"
          :loading="editor.isSubmitting.value"
          @click="save(true)"
        >
          {{ t('insights.sysadmin.config.fullReplace') }}
        </Button>
        <Button :loading="editor.isSubmitting.value" @click="save(false)">
          {{ t('insights.sysadmin.config.save') }}
        </Button>
      </template>
    </PageHeader>

    <div
      v-if="editor.state.value === 'loading'"
      class="config-editor__loading grid gap-lg"
      aria-busy="true"
    >
      <Skeleton v-for="index in 7" :key="index" height="10rem" />
    </div>
    <Alert v-else-if="editor.state.value === 'error'" tone="danger">
      {{ t('insights.sysadmin.config.loadError') }}
      <Button size="sm" variant="ghost" @click="editor.reload">
        {{ t('insights.error.retry') }}
      </Button>
    </Alert>
    <Alert v-else-if="editor.state.value === 'idle'" tone="warning">
      {{ t('insights.sysadmin.config.forbidden') }}
    </Alert>
    <template v-else-if="editor.meta.value">
      <Alert v-if="editor.noticeKey.value" tone="success">
        {{ t(editor.noticeKey.value) }}
      </Alert>
      <Alert v-if="editor.deleteBlockedCount.value > 0" tone="warning">
        {{
          t('insights.sysadmin.config.deleteBlocked', {
            count: editor.deleteBlockedCount.value,
          })
        }}
      </Alert>
      <Alert v-for="message in editor.nonFieldErrors.value" :key="message" tone="danger">
        {{ message.startsWith('insights.') ? t(message) : message }}
      </Alert>

      <div
        class="config-editor__layout grid grid-cols-[minmax(0,_2fr)_minmax(18rem,_1fr)] items-start gap-lg max-lg:grid-cols-[minmax(0,_1fr)]"
      >
        <form class="config-editor__form grid gap-lg" @submit.prevent="save(false)">
          <ConfigOverviewSection
            v-model:form="editor.form.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :disabled="editor.isSubmitting.value"
          />
          <ConfigClassificationSection
            v-model:form="editor.form.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :disabled="editor.isSubmitting.value"
          />
          <ConfigDetectionSection
            v-model:form="editor.form.value"
            v-model:json="editor.jsonEditors.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :disabled="editor.isSubmitting.value"
          />
          <ConfigContentSection
            v-model:form="editor.form.value"
            v-model:json="editor.jsonEditors.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :disabled="editor.isSubmitting.value"
          />
          <ConfigImpactSection
            v-model:form="editor.form.value"
            v-model:json="editor.jsonEditors.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :disabled="editor.isSubmitting.value"
          />
          <ConfigPipelineSection
            v-model:form="editor.form.value"
            v-model:json="editor.jsonEditors.value"
            v-model:pipeline="editor.pipelineEditors.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :warning="editor.llmPipelineWarning.value"
            :disabled="editor.isSubmitting.value"
          />
          <ConfigVisualizationSection
            v-model:form="editor.form.value"
            v-model:json="editor.jsonEditors.value"
            :meta="editor.meta.value"
            :errors="editor.fieldErrors.value"
            :disabled="editor.isSubmitting.value"
          />
        </form>
        <aside
          class="config-editor__preview sticky top-lg min-w-0 p-lg border border-border rounded-lg bg-surface max-lg:static"
        >
          <h2>{{ t('insights.sysadmin.config.payloadPreview') }}</h2>
          <pre v-if="editor.payloadPreview.value.text">{{ editor.payloadPreview.value.text }}</pre>
          <Alert v-else tone="warning">
            {{ t(editor.payloadPreview.value.errorKey) }}
          </Alert>
        </aside>
      </div>
    </template>
  </main>
</template>

<style scoped>
.config-editor .config-editor__preview h2 {
  margin-block: 0 var(--ui-space-md);
  font-size: 1rem;
}
.config-editor .config-editor__preview pre {
  max-height: 42rem;
  margin: 0;
  overflow: auto;
  color: var(--ui-color-fg);
  font-size: 0.75rem;
  white-space: pre-wrap;
}
</style>
